import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { MeasurementUnitDto } from 'src/app/shared/dto/measurement-unit.dto';
import { PackagingTypeDto } from 'src/app/shared/dto/packaging-type.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { PackagingTypes } from '../../services/packaging-types.service';
import { MeasurementunitsService } from '../../services/measurementunits.service';

@Component({
    selector: 'app-packaging-types',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputModule,
        NzFormModule,
        NzIconModule,
        NzCheckboxModule,
        NzDropDownModule,
        NzToolTipModule,
        LoaderComponent,
        NzTabsModule,
        NzButtonModule,
        NzSwitchModule
    ],
    templateUrl: './packaging-types.component.html',
    styleUrl: './packaging-types.component.scss'
})
export class PackagingTypesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    packagingTypeSelected: PackagingTypeDto = new PackagingTypeDto();
    packagingTypes: PackagingTypeDto[] = [];
    isLoading: boolean = true;
    isSaving: boolean = false;

    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    packagingTypeForm: FormGroup<{
        name: FormControl<string>;
        quantityPerPackaging: FormControl<number>;
        batchNumberFormat: FormControl<string>;
        measurementUnitId: FormControl<number | null>;
    }>;

    measurementUnits: MeasurementUnitDto[] = [];

    private readonly packagingTypesService = inject(PackagingTypes);
    private readonly measurementunitsService = inject(MeasurementunitsService);
    private readonly message = inject(NzMessageService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('packagingsTypes', '');
        this.packagingTypeForm = this.fb.group({
            name: ['', [Validators.required]],
            quantityPerPackaging: [0, [Validators.required, Validators.min(1)]],
            batchNumberFormat: [''],
            measurementUnitId: [null as number | null]
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.innerWidth = window.innerWidth;

        await this.packagingTypesService.setupWebSocket();
        this.packagingTypesService.packagingTypes$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (types: PackagingTypeDto[]) => {
                    this.packagingTypes = types;
                    this.packagingTypeSelected = this.packagingTypes.find(pt => pt.id === this.packagingTypeSelected.id) || new PackagingTypeDto();
                },
                error: () => {
                    this.message.error('Error cargando tipos de formato');
                }
            });

        try {
            const response = await this.measurementunitsService.findAll();
            this.measurementUnits = response.measurementUnits;
        } catch (error: any) {
            this.message.error('Error cargando unidades de medida');
        }

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.packagingTypesService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.packagingTypeSelected = new PackagingTypeDto();
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nuevo tipo de formato';
        this.fieldsReadOnly = false;
        this.packagingTypeForm.reset();
        this.packagingTypeForm.enable();
        this.openDrawer();
    }

    view(data: PackagingTypeDto) {
        this.packagingTypeForm.patchValue({
            name: data.name || '',
            quantityPerPackaging: data.quantityPerPackaging || 0,
            batchNumberFormat: data.batchNumberFormat || '',
            measurementUnitId: data.measurementUnitId || null
        });
        this.packagingTypeForm.disable();
        this.packagingTypeSelected = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo tipo de formato ' + this.packagingTypeSelected?.name;
        this.fieldsReadOnly = true;
        this.openDrawer();
    }

    edit(data: PackagingTypeDto) {
        this.packagingTypeSelected = data;
        this.packagingTypeForm.patchValue({
            name: data.name || '',
            quantityPerPackaging: data.quantityPerPackaging || 0,
            batchNumberFormat: data.batchNumberFormat || '',
            measurementUnitId: data.measurementUnitId || null
        });
        this.packagingTypeForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando tipo de formato ' + this.packagingTypeSelected?.name;
        this.fieldsReadOnly = false;
        this.openDrawer();
    }

    cancelEditing() {
        this.closeDrawer();
    }

    async delete(id: number) {
        if (window.confirm('¿Estás seguro de que quieres eliminar este tipo de formato? Esta acción no se puede deshacer.')) {
            try {
                const deletedPackagingType = await this.packagingTypesService.deletePackagingType(id);

                if (!deletedPackagingType) {
                    this.message.error(`No se ha encontrado el tipo de formato ${id}`);
                    return;
                }

                this.message.success('Tipo de formato eliminado correctamente');
            } catch (error: any) {
                this.message.error(`Error eliminando tipo de formato. Error: ${error.message}`);
            }
        } else {
            console.log('Cancelando eliminación de tipo de formato ' + id);
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.packagingTypeSelected = new PackagingTypeDto();
        this.packagingTypeForm.reset();
        this.drawerVisible = false;
    }

    async submitForm() {
        try {
            this.isSaving = true;

            if (!this.packagingTypeForm.valid) {
                this.isSaving = false;
                this.message.error('Por favor, rellena correctamente todos los campos obligatorios.');
                this.packagingTypeForm.markAllAsTouched();
                return;
            }

            const packagingTypeDto: PackagingTypeDto = {
                id: this.packagingTypeSelected.id,
                name: this.packagingTypeForm.controls.name.value,
                quantityPerPackaging: this.packagingTypeForm.controls.quantityPerPackaging.value,
                batchNumberFormat: this.packagingTypeForm.controls.batchNumberFormat.value,
                measurementUnitId: this.packagingTypeForm.controls.measurementUnitId.value || undefined
            };

            if (this.drawerMode === DrawerMode.CREATE) {
                try {
                    const newPackagingType = await this.packagingTypesService.create(packagingTypeDto);

                    if (!newPackagingType) {
                        this.isSaving = false;
                        this.message.error(`Error creando tipo de formato`);
                        return;
                    }

                    this.isSaving = false;
                    this.message.success('Tipo de formato creado correctamente');
                    this.closeDrawer();
                } catch (error: any) {
                    this.isSaving = false;
                    this.message.error(`Error creando tipo de formato. Error: ${error.message}`);
                }
            } else if (this.drawerMode === DrawerMode.EDIT) {
                try {
                    const updatedPackagingType = await this.packagingTypesService.updatePackagingType(packagingTypeDto);

                    if (!updatedPackagingType) {
                        this.isSaving = false;
                        this.message.error('Error actualizando tipo de formato');
                        return;
                    }

                    this.isSaving = false;
                    this.message.success('Tipo de formato actualizado correctamente');
                    this.closeDrawer();
                } catch (error: any) {
                    this.isSaving = false;
                    this.message.error(`Error actualizando tipo de formato. Error: ${error.message}`);
                }
            }
        } catch (e: any) {
            this.isSaving = false;
            this.message.error(`Ha ocurrido un error al guardar el tipo de formato.`);
        }
    }



}
